import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RTooltip, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { Loader2, Euro, TrendingUp, Users, Save, ChevronRight, Settings2, FileSignature } from "lucide-react";
import { API } from "@/lib/api";

const fmt = (n) => "€ " + Math.round(n || 0).toLocaleString("it-IT");
const tierColor = (s) => (s >= 71 ? "#00A859" : s >= 41 ? "#F59E0B" : "#FF2A2A");
const LEVEL_COLOR = { L3: "#FF2A2A", L2: "#F59E0B", L1: "#00A859" };
const STAGE_META = {
  lead: { label: "Lead", color: "#3B82F6", bg: "bg-blue-100 text-blue-800" },
  proposta: { label: "Proposta", color: "#F59E0B", bg: "bg-amber-100 text-amber-800" },
  vinto: { label: "Vinto", color: "#00A859", bg: "bg-emerald-100 text-emerald-800" },
  perso: { label: "Perso", color: "#6B7280", bg: "bg-gray-200 text-gray-600" },
};
const STAGE_ORDER = ["lead", "proposta", "vinto", "perso"];

export default function PipelinePage() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { canEdit } = useAuth();

  const load = async () => {
    try {
      const [p, c] = await Promise.all([api.get("/pipeline"), api.get("/pipeline/config")]);
      setData(p.data); setCfg(c.data.tiers); setError(false);
    } catch (e) {
      setError(true);
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nel caricamento della pipeline");
    }
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    setSaving(true);
    try {
      const payload = {
        L1: { min: Number(cfg.L1.min) || 0, max: Number(cfg.L1.max) || 0 },
        L2: { min: Number(cfg.L2.min) || 0, max: Number(cfg.L2.max) || 0 },
        L3: { min: Number(cfg.L3.min) || 0, max: Number(cfg.L3.max) || 0 },
      };
      await api.put("/pipeline/config", payload);
      toast.success("Importi salvati"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const updCfg = (lvl, field, val) => setCfg((c) => ({ ...c, [lvl]: { ...c[lvl], [field]: val } }));

  const setStage = async (clientId, stage) => {
    try {
      await api.put(`/pipeline/${clientId}/stage`, { stage });
      toast.success(`Stato aggiornato: ${STAGE_META[stage].label}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore aggiornamento stato"); }
  };

  if (!data || !cfg) {
    return <Layout><PageHeader title="Pipeline consulenza" />
      {error ? (
        <div className="p-8 text-center" data-testid="pipeline-error">
          <p className="text-muted-foreground">Impossibile caricare la pipeline.</p>
          <Button className="rounded-none mt-4" onClick={load}>Riprova</Button>
        </div>
      ) : (
        <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      )}
    </Layout>;
  }

  const chartData = ["L3", "L2", "L1"].map((k) => ({ level: cfg[k].label.split("—")[0].trim(), key: k, value: data.per_level[k].total_avg, count: data.per_level[k].count }));

  return (
    <Layout>
      <PageHeader title="Pipeline consulenza" subtitle="Valore commerciale potenziale per cliente">
        {canEdit && <Button variant="outline" className="rounded-none" onClick={() => setShowCfg((s) => !s)} data-testid="toggle-config-btn"><Settings2 className="w-4 h-4 mr-2" /> Configura importi</Button>}
      </PageHeader>

      <div className="p-4 sm:p-8 space-y-4">
        {showCfg && canEdit && (
          <div className="bg-white border border-border p-6" data-testid="pipeline-config">
            <div className="data-label mb-4">Importi per livello di offerta (range min–max €)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {["L3", "L2", "L1"].map((k) => (
                <div key={k} className="border border-border p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3" style={{ backgroundColor: LEVEL_COLOR[k] }} />
                    <span className="text-sm font-medium">{cfg[k].label}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="data-label">Min €</Label><Input type="number" data-testid={`cfg-${k}-min`} className="mt-1 rounded-none font-mono" value={cfg[k].min} onChange={(e) => updCfg(k, "min", e.target.value)} /></div>
                    <div><Label className="data-label">Max €</Label><Input type="number" data-testid={`cfg-${k}-max`} className="mt-1 rounded-none font-mono" value={cfg[k].max} onChange={(e) => updCfg(k, "max", e.target.value)} /></div>
                  </div>
                </div>
              ))}
            </div>
            <Button className="rounded-none mt-4" onClick={saveCfg} disabled={saving} data-testid="save-config-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva importi</Button>
            <p className="text-xs text-muted-foreground mt-2">Soglie: punteggio 0–40 → Livello 3 · 41–70 → Livello 2 · 71–100 → Livello 1.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-primary text-primary-foreground p-6" data-testid="pipeline-total">
            <div className="flex items-center gap-2 opacity-80"><Euro className="w-4 h-4" /><span className="data-label text-primary-foreground/80">Valore pesato (probabilità)</span></div>
            <div className="font-mono font-medium text-4xl mt-2" data-testid="pipeline-weighted-total">{fmt(data.totals.weighted)}</div>
            <div className="text-xs opacity-80 mt-1">valore pieno {fmt(data.totals.avg)} · range {fmt(data.totals.min)} – {fmt(data.totals.max)}</div>
          </div>
          <div className="bg-white border border-border p-6">
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" /><span className="data-label">Clienti in pipeline</span></div>
            <div className="font-mono font-medium text-4xl mt-2">{data.totals.clients_with_data}</div>
            <div className="text-xs text-muted-foreground mt-1">su {data.rows.length} clienti totali</div>
            {data.closing_metrics && (data.closing_metrics.won.count > 0 || data.closing_metrics.lost.count > 0) && (
              <div className="mt-3 pt-3 border-t border-border text-xs space-y-1" data-testid="closing-metrics">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Chiusura media (vinte)</span>
                  <span className="font-mono font-semibold text-[#00A859]" data-testid="avg-days-won">
                    {data.closing_metrics.won.avg_days != null ? `${data.closing_metrics.won.avg_days} gg · ${data.closing_metrics.won.count} tratt.` : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Chiusura media (perse)</span>
                  <span className="font-mono font-semibold" data-testid="avg-days-lost">
                    {data.closing_metrics.lost.avg_days != null ? `${data.closing_metrics.lost.avg_days} gg · ${data.closing_metrics.lost.count} tratt.` : "—"}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="bg-white border border-border p-6">
            <div className="flex items-center gap-2 data-label mb-2"><TrendingUp className="w-3.5 h-3.5 text-primary" /> Valore per livello</div>
            <ResponsiveContainer width="100%" height={110}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="level" tick={{ fontSize: 9 }} stroke="#9CA3AF" />
                <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                <RTooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 0, border: "1px solid #E5E7EB", fontSize: 11 }} />
                <Bar dataKey="value">{chartData.map((e) => <Cell key={e.key} fill={LEVEL_COLOR[e.key]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {data.per_stage && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stage-summary">
            {STAGE_ORDER.map((s) => (
              <div key={s} className="bg-white border border-border p-4" data-testid={`stage-card-${s}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: STAGE_META[s].color }} />
                  <span className="data-label">{STAGE_META[s].label} · {Math.round((data.per_stage[s]?.probability ?? 0) * 100)}%</span>
                </div>
                <div className="font-mono font-medium text-2xl mt-1.5">{fmt(data.per_stage[s]?.weighted)}</div>
                <div className="text-xs text-muted-foreground">{data.per_stage[s]?.count ?? 0} client{(data.per_stage[s]?.count ?? 0) === 1 ? "e" : "i"}</div>
              </div>
            ))}
          </div>
        )}

        <div className="border border-border bg-white overflow-x-auto" data-testid="pipeline-table">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left">
              <th className="data-label px-4 py-3">Cliente</th>
              <th className="data-label px-4 py-3">Settore</th>
              <th className="data-label px-4 py-3 text-right">Punteggio</th>
              <th className="data-label px-4 py-3">Maturità</th>
              <th className="data-label px-4 py-3">Offerta consigliata</th>
              <th className="data-label px-4 py-3">Stato</th>
              <th className="data-label px-4 py-3 text-right">Valore stimato</th>
              <th className="data-label px-4 py-3 text-right">Valore pesato</th>
              <th className="data-label px-4 py-3 text-right">Proposta</th>
              <th className="data-label px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.client_id} data-testid={`pipeline-row-${i}`} onClick={() => navigate(`/clients/${r.client_id}`)} className={`border-b border-border cursor-pointer hover:bg-secondary transition-colors duration-150 ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}>
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.sector || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold" style={{ color: r.overall != null ? tierColor(r.overall) : "#9CA3AF" }}>{r.overall != null ? r.overall : "—"}</td>
                  <td className="px-4 py-3">{r.tier ? <span className="text-[10px] font-bold px-2 py-0.5 text-white" style={{ backgroundColor: r.tier.color }}>{r.tier.label}</span> : <span className="text-xs text-muted-foreground">nessun assessment</span>}</td>
                  <td className="px-4 py-3">{r.level ? <span className="text-[10px] font-bold px-2 py-0.5 text-white" style={{ backgroundColor: LEVEL_COLOR[r.level] }}>{r.level_label}</span> : "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {canEdit ? (
                      <select
                        data-testid={`stage-select-${i}`}
                        value={r.stage}
                        onChange={(e) => setStage(r.client_id, e.target.value)}
                        className={`text-[11px] font-bold px-1.5 py-1 border-0 outline-none cursor-pointer ${STAGE_META[r.stage].bg}`}
                      >
                        {STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGE_META[s].label} ({Math.round((data.per_stage?.[s]?.probability ?? 0) * 100)}%)</option>)}
                      </select>
                    ) : (
                      <span className={`text-[10px] font-bold px-2 py-0.5 ${STAGE_META[r.stage].bg}`}>{r.stage_label}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{r.level ? fmt(r.value_avg) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold" data-testid={`weighted-${i}`}>
                    {r.level ? <>{fmt(r.weighted_value)} <span className="text-[10px] text-muted-foreground font-normal">({r.probability}%)</span></> : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-2">
                      {r.proposal_accepted && <span data-testid={`proposal-accepted-${i}`} className="text-[10px] font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-800">FIRMATA</span>}
                      <button data-testid={`proposal-btn-${i}`} title="Scarica proposta PDF" onClick={(e) => { e.stopPropagation(); window.open(`${API}/clients/${r.client_id}/proposal.pdf`, "_blank"); }} className="text-primary hover:text-primary/70 inline-flex"><FileSignature className="w-4 h-4" /></button>
                    </span>
                  </td>
                  <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
